import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {URGENCY} from '../../../constants/Urgency';
import {
  BUILDER_TYPES,
  SPP_BUILDER_TYPES,
  BuilderTypeValue,
  SppBuilderTypeValue,
} from '../constants/SolutionType';
import {getSetUpNewInitialValues} from '../hooks/builderSetUpNewHooks';
import {getContributeInitialValues} from '../hooks/builderContributeHooks';
import {getModifyInitialValues} from '../hooks/builderModifyHooks';
import {getSavedWithdrawalValues} from '../hooks/builderWithdrawalHooks';
import {getTransferInitialValues} from '../hooks/builderTransferHooks';
import {mainPlanTypeType, builderFieldsType} from './builderFields';

export interface GetBuilderInitialValuesArgs {
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  reviewDetails: ReviewDetailsFragment | undefined;
  searchParams: URLSearchParams;
  isSppDa: boolean;
}

const resolveHow = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  reviewDetails: ReviewDetailsFragment | undefined,
  searchParams: URLSearchParams,
  isSppDa: boolean
): BuilderTypeValue | undefined => {
  // SPP DA is always an instruction flow
  if (isSppDa) {
    return BUILDER_TYPES.Instruction;
  }
  if (builderDetails) {
    return builderDetails.isXo ? BUILDER_TYPES.Instruction : BUILDER_TYPES.Recommendation;
  }
  if (reviewDetails?.executionOnly) {
    return BUILDER_TYPES.Instruction;
  }
  if (reviewDetails) {
    return BUILDER_TYPES.Recommendation;
  }
  return (searchParams.get('how') as BuilderTypeValue | null) ?? undefined;
};

const resolveSppType = (
  builderDetails: OvbuilderDetails_ovrecommendDetails | null,
  searchParams: URLSearchParams
): SppBuilderTypeValue | undefined => {
  if (builderDetails) {
    return builderDetails.isSppDaXo
      ? SPP_BUILDER_TYPES.ExecutionOnly
      : SPP_BUILDER_TYPES.Advised;
  }
  return (searchParams.get('sppType') as SppBuilderTypeValue | null) ?? undefined;
};

export const getBuilderInitialValues = ({
  builderDetails,
  reviewDetails,
  searchParams,
  isSppDa,
}: GetBuilderInitialValuesArgs): Partial<builderFieldsType> => {
  const planAction =
    builderDetails?.action ??
    (searchParams.get('planAction') as OVRecommendActionEnum | null) ??
    undefined;

  const mainPlanType =
    (builderDetails?.planType as builderFieldsType['mainPlanType']) ??
    (searchParams.get('mainPlanType') as mainPlanTypeType | null) ??
    undefined;

  return {
    isSppDa,
    planAction,
    mainPlanType,
    how: resolveHow(builderDetails, reviewDetails, searchParams, isSppDa),
    sppType: resolveSppType(builderDetails, searchParams),
    contact: builderDetails?.contact?.id || searchParams.get('contact') || undefined,
    urgency: builderDetails?.urgency || URGENCY.NORMAL_ID,
    urgencyReason: builderDetails?.urgencyReason ?? undefined,
    investorCategory: reviewDetails?.account?.investorCategory || undefined,
    setUpNew: getSetUpNewInitialValues(builderDetails),
    contribute: getContributeInitialValues(builderDetails, isSppDa),
    modify: getModifyInitialValues(builderDetails),
    withdraw: getSavedWithdrawalValues(builderDetails),
    transfer: getTransferInitialValues(builderDetails, isSppDa),
  };
};
